import React, { Suspense, useContext } from "react";
import { Route } from "react-router-dom";
import { Admin, CustomRoutes, Loading } from "react-admin";
import { authConfigServiceFactory } from "../logic/auth/auth-config.service";
import { AuthRequired } from "../components/auth/auth-required";
import { useAuAuthProvider } from "../logic/auth/use-au-auth-provider";
import { useAuDataProvider } from "../logic/data-provider/use-au-data-provider";
import { UsersResource } from "../components/users/users";
import { getAllPermissions } from "../components/auth/crud-permissions";
import { AuPolicy } from "@arrowsup/platform-dtos";
import { OrgConfigPage } from "../components/org-config/org-config-page";
import { useOrgConfigService } from "../logic/org-config/org-config.service";
import { OauthRedirect } from "../components/integrations/oauth/OauthRedirect";
import { BrowserRouter } from "react-router-dom";
import { useApi } from "../logic/api/use-api";
import { AuLayout } from "../components/menu/AuMenu";
import { RouteElement } from "../components/util/RouteElement";
import { useOauthServices } from "../components/integrations/oauth/use-oauth-services";
import { Box } from "@mui/material";
import { ApiContext } from "../logic/api/api-context";
import { auTheme } from "./theme";
import { ErrorBoundary } from "react-error-boundary";
import { useAppConfigService } from "../logic/app-config/use-app-config.service";
import { Conversion } from "../components/conversions/conversion";
import { FatalError } from "../components/feedback/fatal-error";
import { ConversionFilterList } from "../components/conversions/conversion-filter-list/conversion-filter-list";
import { ConversionOutputStream } from "../components/conversions/conversion-output-stream/conversion-output-stream";
import { ConversionInputStream } from "../components/conversions/conversion-input-stream/conversion-input-stream";
import { QueryClient } from "@tanstack/react-query";
import { CustomTrackingParamResource } from "../components/conversions/tracking/custom-tracking-param";
import { PaidSourceResource } from "../components/conversions/tracking/paid-source";
import { TrackedCallResource } from "../components/conversions/tracking/tracked-call";
import { TrackedUrlResource } from "../components/conversions/tracking/tracked-url";
import { FunnelStageResource } from "../components/conversions/funnel/funnel-stage";
import { FunnelStageAssignmentResource } from "../components/conversions/funnel/funnel-stage-assignment";
import { DashboardResource } from "../components/dashboard/dashboard";
import { RetainerResource } from "../components/retainer/retainer";

// This causes annoying refreshes on the dashboard.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * The Core App. It looks up the permissions for the user based upon that users
 * permissions. Then shows/hides the appropriate resources.
 * @returns
 */
const CoreApp: React.FC = () => {
  const oauthServices = useOauthServices();
  const authProvider = useAuAuthProvider();
  const dataProvider = useAuDataProvider();
  const api = useContext(ApiContext).api;
  const acs = useAppConfigService(api);
  const orgConfigService = useOrgConfigService(api);

  return (
    // need this special router tag to remove the hashtags from the urls,
    // because you can't input urls with hashtags into Zoho's allowed oauth urls
    // input box
    <BrowserRouter>
      <Admin // the actual ReactAdmin application
        theme={auTheme}
        dataProvider={dataProvider}
        authProvider={authProvider}
        queryClient={queryClient}
        layout={AuLayout} // needed to create custom layout in order to modify what was in the menu
      >
        {(permissions: AuPolicy[]) => {
          // magical framework parameter: https://marmelab.com/react-admin/Permissions.html#restricting-access-to-resources-or-views
          // a record that has permissions for all AuResourceNames
          const allPermissions = getAllPermissions(permissions);

          return (
            <>
              {allPermissions["dashboard"].read
                ? DashboardResource(allPermissions["dashboard"])
                : null}
              {allPermissions["conversion"].read
                ? Conversion(allPermissions["conversion"])
                : null}
              {allPermissions["conversion-filter"].read
                ? ConversionFilterList(allPermissions["conversion-filter"])
                : null}
              {allPermissions["conversion-output-stream"].read
                ? ConversionOutputStream(
                    allPermissions["conversion-output-stream"]
                  )
                : null}
              {allPermissions["conversion-input-stream"].read
                ? ConversionInputStream(
                    allPermissions["conversion-input-stream"]
                  )
                : null}
              {allPermissions["users"].read
                ? UsersResource(allPermissions["users"])
                : null}
              {allPermissions["paid-source"].read
                ? PaidSourceResource(allPermissions["paid-source"])
                : null}
              {allPermissions["tracked-call"].read
                ? TrackedCallResource(allPermissions["tracked-call"])
                : null}
              {allPermissions["tracked-url"].read
                ? TrackedUrlResource(allPermissions["tracked-url"])
                : null}
              {allPermissions["custom-tracking-param"].read
                ? CustomTrackingParamResource(
                    allPermissions["custom-tracking-param"]
                  )
                : null}
              {allPermissions["funnel-stage"].read
                ? FunnelStageResource(allPermissions["funnel-stage"])
                : null}
              {allPermissions["funnel-stage-assignment"].read
                ? FunnelStageAssignmentResource(
                    allPermissions["funnel-stage-assignment"]
                  )
                : null}
              {allPermissions["retainer"].read
                ? RetainerResource(allPermissions["retainer"])
                : null}
              <CustomRoutes>
                <Route
                  path="/config/*"
                  element={
                    <Box sx={{ marginTop: "2rem" }}>
                      <RouteElement title="Configuration">
                        <ErrorBoundary fallback={<FatalError />}>
                          <Suspense fallback={<Loading />}>
                            <OrgConfigPage
                              orgConfigService={orgConfigService}
                              oauthServices={oauthServices}
                              acs={acs}
                            />
                          </Suspense>
                        </ErrorBoundary>
                      </RouteElement>
                    </Box>
                  }
                />
                <Route
                  path="/oauth-redirect/intuit"
                  element={
                    <RouteElement title="Intuit Oauth Redirect">
                      <OauthRedirect oauthService={oauthServices.intuit} />
                    </RouteElement>
                  }
                />
                <Route
                  path="/oauth-redirect/zoho"
                  element={
                    <RouteElement title="Zoho Oauth Redirect">
                      <OauthRedirect oauthService={oauthServices.zoho} />
                    </RouteElement>
                  }
                />
                <Route
                  path="/oauth-redirect/google"
                  element={
                    <RouteElement title="Google Oauth Redirect">
                      <OauthRedirect oauthService={oauthServices.google} />
                    </RouteElement>
                  }
                />
                <Route
                  path="/oauth-redirect/microsoft"
                  element={
                    <RouteElement title="Microsoft Oauth Redirect">
                      <OauthRedirect oauthService={oauthServices.microsoft} />
                    </RouteElement>
                  }
                />
              </CustomRoutes>
            </>
          );
        }}
      </Admin>
    </BrowserRouter>
  );
};

/** Add ApiContext, which the app needs. */
const CoreAppBootstrap: React.FC = () => {
  const api = useApi();

  return (
    <ApiContext.Provider value={{ api }}>
      <CoreApp />
    </ApiContext.Provider>
  );
};

const App = () => {
  const authConfigService = authConfigServiceFactory();

  return (
    <AuthRequired authConfigService={authConfigService}>
      <CoreAppBootstrap />
    </AuthRequired>
  );
};

export default App;
